import { useMemo, useState, type ReactNode } from "react";
import { Plus, Trash2 } from "lucide-react";

import type { DesiredPool } from "@/api/fleet";
import { ConfirmModal } from "@/components/ConfirmModal";
import { Checkbox, Input } from "@/components/form";
import { MenuItem } from "@/components/KebabMenu";
import { Button, EmptyState, Table, UnsavedBadge, type TableColumn } from "@/components/ui";
import { useDirtyValues } from "@/hooks/useDirtyValues";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";

/**
 * Édition des pools de la flotte de build.
 *
 * Rien n'est appliqué depuis ici. Le formulaire produit une liste, l'API la
 * valide, et une pull request est ouverte sur le dépôt : c'est un humain qui
 * relit, fusionne, puis déploie la flotte à la main.
 *
 * Le détour paraît lourd pour changer une taille de disque. Il évite deux
 * choses. D'abord qu'un jeton de longue durée capable de réécrire le control
 * plane existe quelque part — GitHub Free n'offrant aucune protection de
 * branche sur les dépôts privés, il n'y aurait rien pour l'encadrer. Ensuite
 * que la flotte réelle cesse de ressembler à ce que le dépôt décrit, ce qui
 * rendrait ses tests muets.
 */

const FIELDS: { key: keyof DesiredPool; label: string; hint?: string }[] = [
  { key: "label", label: "Label", hint: "ps-… no internal dash" },
  { key: "instanceType", label: "Type", hint: "e.g. c7g.xlarge" },
  { key: "storageGib", label: "Disk (GiB)" },
  { key: "purpose", label: "Purpose" },
];

function emptyPool(): DesiredPool {
  return {
    label: "",
    instanceType: "",
    storageGib: 40,
    spot: true,
    purpose: "",
  };
}

function samePool(a: DesiredPool, b: DesiredPool): boolean {
  return (
    a.label === b.label &&
    a.instanceType === b.instanceType &&
    a.storageGib === b.storageGib &&
    a.spot === b.spot &&
    a.purpose === b.purpose
  );
}

/** A pool of the draft, with its place in the list (the draft has no other identity: a label is editable). */
interface DraftRow {
  pool: DesiredPool;
  index: number;
}

export function PoolEditor({
  current,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  current: DesiredPool[];
  pending: boolean;
  /** Where the failure of the proposal is said: here, beside the button that failed, not at the top of a long page. */
  error?: ReactNode;
  onSubmit: (pools: DesiredPool[]) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<DesiredPool[]>(() =>
    current.map((pool) => ({ ...pool })),
  );
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);

  // The draft differs from the one this editor was OPENED with — not from ``current``, which is what is
  // live and moves when the fleet is read again: a refetch must not make an untouched draft "unsaved".
  // ``changed`` below (the draft against ``current``) is what there is to propose. The way out of the
  // page is held while the draft holds edits, and Cancel — which throws them away without leaving —
  // asks first, like every other way of losing them.
  const { dirty } = useDirtyValues(draft);
  useUnsavedChanges(dirty);

  const removed = useMemo(
    () =>
      current
        .filter((pool) => !draft.some((d) => d.label === pool.label))
        .map((pool) => pool.label),
    [current, draft],
  );
  const added = useMemo(
    () =>
      draft
        .filter((pool) => pool.label && !current.some((c) => c.label === pool.label))
        .map((pool) => pool.label),
    [current, draft],
  );
  const modified = useMemo(
    () =>
      draft
        .filter((pool) => {
          const before = current.find((c) => c.label === pool.label);
          return before && !samePool(before, pool);
        })
        .map((pool) => pool.label),
    [current, draft],
  );

  const changed = removed.length + added.length + modified.length > 0;

  function update(index: number, key: keyof DesiredPool, value: string | boolean) {
    setDraft((pools) =>
      pools.map((pool, i) =>
        i === index
          ? {
              ...pool,
              [key]:
                key === "storageGib"
                  ? Number.parseInt(String(value), 10) || 0
                  : value,
            }
          : pool,
      ),
    );
  }

  const columns: TableColumn<DraftRow>[] = [
    ...FIELDS.map(
      (field): TableColumn<DraftRow> => ({
        key: String(field.key),
        header: field.label,
        // No Field here: the column header is the visible label, so each cell
        // says which pool and which column it is.
        cell: ({ pool, index }) => (
          <Input
            size="sm"
            aria-label={`${field.label}, pool ${index + 1}`}
            type={field.key === "storageGib" ? "number" : "text"}
            value={String(pool[field.key] ?? "")}
            placeholder={field.hint}
            onChange={(e) => update(index, field.key, e.target.value)}
          />
        ),
      }),
    ),
    {
      key: "market",
      header: "Market",
      cell: ({ pool, index }) => (
        <Checkbox
          label="spot"
          aria-label={`Spot, pool ${index + 1}`}
          checked={pool.spot}
          onChange={(e) => update(index, "spot", e.target.checked)}
        />
      ),
    },
  ];

  return (
    // No card around it: the table draws its own frame, and a frame inside a frame
    // is two greys where the eye expects one.
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-ink">
          Edit pools
        </h3>
        <p className="mt-1 text-xs text-ink-muted">
          A proposal is opened for review. It only reaches the fleet
          after merging, then deployment.
        </p>
      </div>

      <Table
        label="Pools being edited"
        columns={columns}
        rows={draft.map((pool, index) => ({ pool, index }))}
        rowKey={({ index }) => String(index)}
        empty={<EmptyState title="No pools">Add one below.</EmptyState>}
        actions={{
          label: ({ index }) => `Actions for pool ${index + 1}`,
          // Taking a pool out of the DRAFT changes nothing yet: the gate is the
          // "Propose" confirmation below, which asks for the labels being removed.
          items: ({ index }) => (
            <MenuItem danger icon={Trash2} onClick={() => setDraft((pools) => pools.filter((_, i) => i !== index))}>
              Remove pool
            </MenuItem>
          ),
        }}
      />

      {error}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={Plus}
            onClick={() => setDraft((pools) => [...pools, emptyPool()])}
          >
            Add a pool
          </Button>
          <UnsavedBadge dirty={dirty} />
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={pending} onClick={() => (dirty ? setDiscarding(true) : onCancel())}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={!changed}
            onClick={() => setConfirming(true)}
          >
            {pending ? "Sending…" : "Propose"}
          </Button>
        </div>
      </div>

      {changed && (
        <ul className="space-y-0.5 text-xs text-ink-muted">
          {added.map((label) => (
            <li key={`a-${label}`}>Added: {label}</li>
          ))}
          {modified.map((label) => (
            <li key={`m-${label}`}>Modified: {label}</li>
          ))}
          {removed.map((label) => (
            <li key={`r-${label}`} className="text-warning-fg">
              Removed or renamed: {label}
            </li>
          ))}
        </ul>
      )}

      {discarding && (
        <ConfirmModal
          title="Discard your changes?"
          confirmLabel="Discard"
          cancelLabel="Keep editing"
          danger
          onCancel={() => setDiscarding(false)}
          onConfirm={() => {
            setDiscarding(false);
            onCancel();
          }}
        >
          <p>The pools you edited will go back to what the repository declares. Nothing has been proposed.</p>
        </ConfirmModal>
      )}

      {confirming && (
        <ConfirmModal
          title="Propose this change?"
          confirmLabel="Open the proposal"
          pendingLabel="Sending…"
          pending={pending}
          danger={removed.length > 0}
          // Une suppression est ce qui peut faire le plus de dégâts, et le
          // dégât est silencieux : un job dont l'étiquette n'existe plus
          // n'échoue pas, il attend jusqu'au délai de 24 h. D'où la phrase à
          // recopier — la même précaution que pour détruire un environnement.
          confirmPhrase={removed.length > 0 ? removed.join(",") : undefined}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            onSubmit(draft);
          }}
        >
          {removed.length > 0 ? (
            <div className="space-y-2">
              <p>
                You're removing {removed.join(", ")}. Renaming a label
                is equivalent to deleting one: the old one disappears.
              </p>
              <p>
                A job requesting a label that no longer exists{" "}
                <strong>doesn't fail</strong> — it stays queued
                until the 24h timeout, with nothing to explain why.
                Check that no repo still requests it.
              </p>
            </div>
          ) : (
            <p>
              A pull request will be opened. Nothing changes on the fleet
              until it is merged and then deployed.
            </p>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}
